/* eslint-disable react-hooks/exhaustive-deps */

import { Box, Button, Flex, Menu, Stack, Tabs, Text } from '@chakra-ui/react';
import { CaretDown, CaretLeft, Check, MapPin, Share, Suitcase } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { fetchAPI } from '@/api/fetch';
import {
  apiGetJobDetails,
  apiUpdateJobApplicationQuestions,
  apiUpdateJobPublishJobBoard,
  apiUpdateJobStatus,
  GetJobDetailsResponse
} from '@/api/services';
import { Step } from '@/components/data-display/Step';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import useAuth from '@/store/auth';
import { ApplicationQuestion } from '@/types/ApplicationQuestion';
import { CandidateWithMatchInfo } from '@/types/Candidate';
import { Job } from '@/types/Job';
import { getEmploymentTypeLabel, JobStatus } from '@/types/enums';
import { getJobBoardJobUrl, openInNewTab } from '@/utils';
import ApplicationQuestions from './ApplicationQuestions';
import ApplicationQuestionsDialog from './ApplicationQuestionsDialog';
import CandidateList from './CandidateList';
import EditJobDialog from './EditJobDialog';
import JobSummary from './JobSummary';
import PotentialCandidates from './PotentialCandidates';
import PublishJobDialog from './PublishJobDialog';

export default function JobDetails() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const [job, setJob] = useState<Job | null>(null);
  const [totalCandidates, setTotalCandidates] = useState<number>(0);
  const [waitingReview, setWaitingReview] = useState<number>(0);
  const [candidatePassScreening, setCandidatePassScreening] = useState<number>(0);
  const [tab, setTab] = useState<string>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('tab') || 'summary';
  });
  const [archived, setArchived] = useState<boolean>(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState<boolean>(false);
  const [pipelineCandidateIds, setPipelineCandidateIds] = useState<string[]>([]);
  const [matchCandidates, setMatchCandidates] = useState<CandidateWithMatchInfo[]>([]);
  const [candidateListRefresh, setCandidateListRefresh] = useState<(() => void) | null>(null);
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState<boolean>(false);
  const [pendingPublishStatus, setPendingPublishStatus] = useState<boolean>(false);
  const [isPublishUpdating, setIsPublishUpdating] = useState<boolean>(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [isApplicationQuestionsDialogOpen, setIsApplicationQuestionsDialogOpen] =
    useState<boolean>(false);
  const [pendingCategory, setPendingCategory] = useState<string | undefined>(undefined);
  const [isQuestionsUpdating, setIsQuestionsUpdating] = useState<boolean>(false);
  const [questionsError, setQuestionsError] = useState<string | null>(null);

  const fetchJobDetails = async () => {
    if (!jobId) {
      navigate('/job');
      return;
    }

    try {
      const response = await fetchAPI<GetJobDetailsResponse>({
        request: () => apiGetJobDetails({ jobId })
      });

      if (response.success) {
        const {
          job_info,
          total_candidates,
          waiting_review,
          candidate_pass_screening,
          match_candidates
        } = response.data;
        setJob(job_info);
        setTotalCandidates(total_candidates);
        setWaitingReview(waiting_review);
        setCandidatePassScreening(candidate_pass_screening);
        setMatchCandidates(match_candidates);
        setArchived(job_info.status === JobStatus.Archived);
      } else {
        console.error('Failed to fetch job details:', response.message);
        navigate('/job');
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
      navigate('/job');
    }
  };

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  const onUpdateJob = (updatedJob: Job) => {
    setJob(updatedJob);
  };

  const getStatusList = () => {
    const status = ['In Progress'];
    if (job?.status === JobStatus.Archived) {
      status.push('Archived');
    } else {
      status.push('Completed');
    }
    return status;
  };

  const getStatusIndex = () => {
    if (job?.status === JobStatus.Archived) {
      return 1;
    }
    if (job?.status === JobStatus.Completed) {
      return 1;
    }
    return 0;
  };

  const updateStatus = async (status: JobStatus) => {
    if (!job || isStatusUpdating) return;

    setIsStatusUpdating(true);

    try {
      const response = await fetchAPI({
        request: () =>
          apiUpdateJobStatus({
            jobId: job.job_id,
            payload: { status }
          })
      });

      if (response.success) {
        setArchived(status === JobStatus.Archived);
        setJob((prevJob) => ({ ...prevJob!, status }));
      } else {
        console.error('Failed to update job status:', response.message);
      }
    } catch (error) {
      console.error('Error updating job status:', error);
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const handleCandidateAdded = (addedCandidateId?: string) => {
    // Immediately remove the candidate from matchCandidates list for better UX
    if (addedCandidateId) {
      setMatchCandidates((prev) =>
        prev.filter((candidate) => candidate.candidate_id !== addedCandidateId)
      );
    }

    // Refresh job details to get updated data
    fetchJobDetails();
    // Also refresh the candidate list to show the newly added candidate
    candidateListRefresh?.();
  };

  const handleCandidatesChange = (candidateIds: string[]) => {
    setPipelineCandidateIds(candidateIds);
  };

  const handleCandidateListRefresh = (refreshFn: () => void) => {
    setCandidateListRefresh(() => refreshFn);
  };

  const handlePublishToggle = (checked: boolean) => {
    setPendingPublishStatus(checked);
    setPublishError(null);
    setIsPublishDialogOpen(true);
  };

  const handlePublishConfirm = async (category?: string) => {
    if (!job) return;

    // If publishing (not unpublishing), show application questions dialog
    if (pendingPublishStatus) {
      setPendingCategory(category);
      setIsPublishDialogOpen(false);
      setIsApplicationQuestionsDialogOpen(true);
      return;
    }

    // If unpublishing, proceed directly
    setIsPublishUpdating(true);
    setPublishError(null);

    try {
      const response = await fetchAPI({
        request: () =>
          apiUpdateJobPublishJobBoard({
            jobId: job.job_id,
            payload: { published: pendingPublishStatus, category }
          })
      });

      if (response.success) {
        setJob((prevJob) => ({
          ...prevJob!,
          publish_job_board: pendingPublishStatus,
          category: category || prevJob!.category,
          timestamp: {
            ...prevJob!.timestamp,
            published_at: pendingPublishStatus ? Math.floor(Date.now() / 1000) : null
          }
        }));
        setIsPublishDialogOpen(false);
      } else {
        setPublishError(response.message || 'Failed to update publish status');
      }
    } catch (error) {
      console.error('Error updating publish status:', error);
      setPublishError('An error occurred while updating publish status');
    } finally {
      setIsPublishUpdating(false);
    }
  };

  const handlePublishCancel = () => {
    setIsPublishDialogOpen(false);
    setPublishError(null);
  };

  const handleApplicationQuestionsConfirm = async (questions: ApplicationQuestion[]) => {
    if (!job) return;

    setIsQuestionsUpdating(true);
    setQuestionsError(null);

    try {
      // First, update application questions
      const questionsResponse = await fetchAPI({
        request: () =>
          apiUpdateJobApplicationQuestions({
            jobId: job.job_id,
            payload: { application_questions: questions }
          })
      });

      if (!questionsResponse.success) {
        setQuestionsError(questionsResponse.message || 'Failed to update application questions');
        return;
      }

      // Then, publish the job
      const publishResponse = await fetchAPI({
        request: () =>
          apiUpdateJobPublishJobBoard({
            jobId: job.job_id,
            payload: { published: true, category: pendingCategory }
          })
      });

      if (publishResponse.success) {
        setJob((prevJob) => ({
          ...prevJob!,
          publish_job_board: true,
          category: pendingCategory || prevJob!.category,
          application_questions: questions,
          timestamp: {
            ...prevJob!.timestamp,
            published_at: Math.floor(Date.now() / 1000)
          }
        }));
        setIsApplicationQuestionsDialogOpen(false);
        setPendingCategory(undefined);
      } else {
        setQuestionsError(publishResponse.message || 'Failed to publish job');
      }
    } catch (error) {
      console.error('Error in publish flow:', error);
      setQuestionsError('An error occurred during the publish process');
    } finally {
      setIsQuestionsUpdating(false);
    }
  };

  const handleApplicationQuestionsCancel = () => {
    setIsApplicationQuestionsDialogOpen(false);
    setQuestionsError(null);
    setPendingCategory(undefined);
    // Reopen publish dialog if user cancels questions
    setIsPublishDialogOpen(true);
  };

  const getCreatedDateFromNow = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <Flex w="100%" flexDir="column" p="40px 48px">
      {job ? (
        <>
          <Flex justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" justifyContent="center" h="100%" gap="16px">
              <Box as="button" onClick={() => navigate(`/job`)} cursor="pointer">
                <CaretLeft size={28} color="#545454" />
              </Box>
              <Box fontSize="16px" color="neutrals.700" display="flex" alignItems="center">
                <Text
                  as="span"
                  cursor="pointer"
                  onClick={() => navigate(`/job`)}
                  _hover={{ textDecoration: 'underline' }}
                  mr="4px"
                >
                  Job Management
                </Text>
                <Text as="span" mx="4px">
                  /
                </Text>
                <Text
                  as="span"
                  cursor="pointer"
                  onClick={() => navigate(`/job?client_id=${job.client.client_id}`)}
                  _hover={{ textDecoration: 'underline' }}
                >
                  {job.client.name}
                </Text>
                <Text as="span" mx="4px">
                  /
                </Text>
                <Text
                  as="span"
                  cursor="pointer"
                  fontWeight="bold"
                  onClick={() => navigate(`/job/details/${jobId}?tab=candidates`)}
                  _hover={{ textDecoration: 'underline' }}
                >
                  {job.job_title} {archived ? '(Archived)' : ''}
                </Text>
              </Box>
            </Stack>
            <Flex gap="16px" alignItems="center">
              <Menu.Root>
                <Menu.Trigger asChild>
                  <Button
                    bg="background.secondary"
                    border="1px solid"
                    borderColor="primary.900"
                    borderRadius="8px"
                    color="primary.900"
                    fontSize="14px"
                    loading={isStatusUpdating}
                    loadingText="Updating..."
                  >
                    Update Status
                    <CaretDown size={20} color="#4E76FF" />
                  </Button>
                </Menu.Trigger>
                <Menu.Positioner>
                  <Menu.Content>
                    {(() => {
                      const statusOptions = {
                        [JobStatus.InProgress]: [
                          {
                            value: 'complete',
                            label: 'Complete',
                            status: JobStatus.Completed
                          },
                          {
                            value: 'archive',
                            label: 'Archive',
                            status: JobStatus.Archived
                          }
                        ],
                        [JobStatus.Completed]: [
                          {
                            value: 'incomplete',
                            label: 'Incomplete',
                            status: JobStatus.InProgress
                          }
                        ],
                        [JobStatus.Archived]: [
                          {
                            value: 'restore',
                            label: 'Restore',
                            status: JobStatus.InProgress
                          }
                        ]
                      };
                      return statusOptions[job.status] || [];
                    })().map((item) => (
                      <Menu.Item
                        key={item.value}
                        value={item.value}
                        _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                        fontSize="14px"
                        onClick={() => updateStatus(item.status)}
                      >
                        <Box flex="1">{item.label}</Box>
                      </Menu.Item>
                    ))}
                  </Menu.Content>
                </Menu.Positioner>
              </Menu.Root>
              <EditJobDialog
                job={job}
                onUpdateJob={onUpdateJob}
                disabled={job.status != JobStatus.InProgress}
              />
            </Flex>
          </Flex>
          <Box h="12px" />
          <Flex justifyContent="space-between" gap="16px">
            <Box
              bg="background.secondary"
              border="1px solid"
              borderColor="neutrals.200"
              borderRadius="8px"
              flex="3"
              h="177px"
              p="32px"
              maxW="100%"
              minW="0"
              boxShadow="sm"
              _hover={{ boxShadow: 'md' }}
              transition="all 0.2s ease-in-out"
            >
              <Flex flexDir="column" justifyContent="flex-start" alignItems="flex-start" h="100%">
                <Text
                  maxW="100%"
                  minW="0"
                  fontSize="32px"
                  fontWeight="bold"
                  mb="12px"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  title={job.job_title}
                >
                  {job.job_title}
                </Text>
                <Flex fontSize="14px" color="neutrals.700" alignItems="center" mb="8px">
                  <MapPin size={20} />
                  <Text ml="8px">{job.location}</Text>
                </Flex>
                <Flex fontSize="14px" color="neutrals.700" alignItems="center">
                  <Suitcase size={20} />
                  <Text ml="8px">{getEmploymentTypeLabel(job.employment_type)}</Text>
                </Flex>
              </Flex>
            </Box>
            <Box
              bg="background.secondary"
              border="1px solid"
              borderColor="neutrals.200"
              borderRadius="8px"
              flex="2"
              p="24px"
              boxShadow="sm"
              _hover={{ boxShadow: 'md' }}
              transition="all 0.2s ease-in-out"
            >
              <Flex flexDir="column" alignItems="flex-start" h="100%">
                <Flex alignItems="center" mb="32px" w="100%" justifyContent="space-between">
                  <Text fontSize="20px" color="neutrals.800" fontWeight="bold">
                    Overall Status
                  </Text>
                  {job.status === JobStatus.InProgress && (
                    <Text fontSize="14px" color="neutrals.700" fontStyle="italic">
                      {(() => {
                        const days = getCreatedDateFromNow(job.created_at);
                        return days <= 0
                          ? 'Active today'
                          : `Active for ${days} day${days > 1 ? 's' : ''}`;
                      })()}
                    </Text>
                  )}
                </Flex>
                <Box flex="1" w="100%">
                  <Step steps={getStatusList()} currentStep={getStatusIndex()} />
                </Box>
              </Flex>
            </Box>
          </Flex>
          <Box h="24px" />
          <Box
            bg="background.secondary"
            border="1px solid"
            borderColor="neutrals.200"
            borderRadius="12px"
            flex="1"
            h="140px"
            boxShadow="sm"
            _hover={{ boxShadow: 'md' }}
            transition="all 0.2s ease-in-out"
          >
            <Flex flexDir="column" justifyContent="top" alignItems="left" h="100%" p="32px">
              <Tabs.Root value={tab} onValueChange={(e) => setTab(e.value)} colorPalette="blue">
                <Flex justifyContent="space-between" alignItems="center">
                  <Tabs.List>
                    <Tabs.Trigger value="summary" asChild>
                      <Box
                        fontSize="18px"
                        color="neutrals.900"
                        fontWeight={tab === 'summary' ? 'bold' : 'normal'}
                      >
                        Summary
                      </Box>
                    </Tabs.Trigger>
                    <Tabs.Trigger value="candidates" asChild>
                      <Box
                        fontSize="18px"
                        color="neutrals.900"
                        fontWeight={tab === 'candidates' ? 'bold' : 'normal'}
                      >
                        Candidate List
                      </Box>
                    </Tabs.Trigger>
                    <Tabs.Trigger value="potential" asChild>
                      <Box
                        fontSize="18px"
                        color="neutrals.900"
                        fontWeight={tab === 'potential' ? 'bold' : 'normal'}
                      >
                        Potential Candidates
                      </Box>
                    </Tabs.Trigger>
                    <Tabs.Trigger value="questions" asChild>
                      <Box
                        fontSize="18px"
                        color="neutrals.900"
                        fontWeight={tab === 'questions' ? 'bold' : 'normal'}
                      >
                        Application Questions
                      </Box>
                    </Tabs.Trigger>
                  </Tabs.List>
                  <Flex alignItems="center" gap="24px">
                    <Stack alignItems="end" gap="4px">
                      <Flex alignItems="center" gap="12px">
                        <Text
                          fontSize="16px"
                          color={job.publish_job_board ? 'success.700' : 'neutrals.700'}
                        >
                          {job.publish_job_board ? 'Published' : 'Publish to Job Board'}
                        </Text>
                        {job.publish_job_board ? (
                          <Box
                            as="button"
                            p="4px"
                            border="1.5px solid"
                            borderColor="success.700"
                            borderRadius="full"
                            position="relative"
                            cursor="pointer"
                            color="success.700"
                            onClick={() =>
                              !archived &&
                              job.status === JobStatus.InProgress &&
                              handlePublishToggle(!job.publish_job_board)
                            }
                          >
                            <Check weight="bold" />
                          </Box>
                        ) : (
                          <Box
                            as="button"
                            w="44px"
                            h="24px"
                            bg={job.publish_job_board ? 'primary.900' : 'neutrals.300'}
                            borderRadius="12px"
                            position="relative"
                            cursor={
                              archived || job.status !== JobStatus.InProgress
                                ? 'not-allowed'
                                : 'pointer'
                            }
                            transition="background-color 0.2s"
                            opacity={archived || job.status !== JobStatus.InProgress ? 0.5 : 1}
                            onClick={() =>
                              !archived &&
                              job.status === JobStatus.InProgress &&
                              handlePublishToggle(!job.publish_job_board)
                            }
                          >
                            <Box
                              position="absolute"
                              top="2px"
                              left={job.publish_job_board ? '22px' : '2px'}
                              w="20px"
                              h="20px"
                              bg="white"
                              borderRadius="50%"
                              transition="left 0.2s"
                              boxShadow="sm"
                            />
                          </Box>
                        )}
                      </Flex>
                      {job.publish_job_board && job.timestamp?.published_at && (
                        <Text fontSize="12px" color="neutrals.500" fontStyle="italic">
                          Published:{' '}
                          {new Date(job.timestamp.published_at * 1000).toLocaleDateString()}
                        </Text>
                      )}
                    </Stack>
                    {job.publish_job_board && auth.account?.agency.agency_id && (
                      <Button
                        variant="ghost"
                        color="neutrals.700"
                        p="0"
                        _hover={{ color: 'primary.700', bg: 'transparent' }}
                        onClick={() =>
                          openInNewTab(
                            getJobBoardJobUrl(auth.account!.agency.agency_id, job.job_id)
                          )
                        }
                        aria-label="Open job board in new tab"
                      >
                        <Share size={24} />
                      </Button>
                    )}
                  </Flex>
                </Flex>

                <Tabs.Content value="summary">
                  <JobSummary
                    job={job}
                    totalCandidates={totalCandidates}
                    waitingReview={waitingReview}
                    PassScreening={candidatePassScreening}
                  />
                </Tabs.Content>
                <Tabs.Content value="candidates">
                  <CandidateList
                    jobId={jobId!}
                    archived={archived}
                    onCandidatesChange={handleCandidatesChange}
                    onRefresh={handleCandidateListRefresh}
                  />
                </Tabs.Content>
                <Tabs.Content value="potential">
                  <PotentialCandidates
                    jobId={jobId!}
                    pipelineCandidateIds={pipelineCandidateIds}
                    candidates={matchCandidates}
                    onCandidateAdded={handleCandidateAdded}
                  />
                </Tabs.Content>
                <Tabs.Content value="questions">
                  <ApplicationQuestions job={job} onUpdate={onUpdateJob} />
                </Tabs.Content>
              </Tabs.Root>
            </Flex>
          </Box>
          <PublishJobDialog
            isOpen={isPublishDialogOpen}
            onClose={handlePublishCancel}
            onConfirm={handlePublishConfirm}
            job={job}
            isPublishing={pendingPublishStatus}
            isLoading={isPublishUpdating}
            error={publishError}
          />
          <ApplicationQuestionsDialog
            isOpen={isApplicationQuestionsDialogOpen}
            onClose={handleApplicationQuestionsCancel}
            onConfirm={handleApplicationQuestionsConfirm}
            initialQuestions={job?.application_questions || []}
            isLoading={isQuestionsUpdating}
            error={questionsError}
          />
        </>
      ) : (
        <>
          <Flex justifyContent="space-between" alignItems="center" mb="12px">
            <SkeletonText noOfLines={1} w="50%" />
            <Skeleton borderRadius="8px" h="40px" w="80px" />
          </Flex>
          <Flex justifyContent="space-between" mb="20px">
            <Box
              bg="background.secondary"
              border="1px solid"
              borderColor="neutrals.200"
              borderRadius="8px"
              flex="3"
              h="177px"
              p="32px"
            >
              <SkeletonText noOfLines={3} gap="4" />
            </Box>
            <Box w="16px" />
            <Box
              bg="background.secondary"
              border="1px solid"
              borderColor="neutrals.200"
              borderRadius="8px"
              flex="2"
              p="32px"
            >
              <SkeletonText noOfLines={3} gap="4" />
            </Box>
          </Flex>
          <Box
            bg="background.secondary"
            border="1px solid"
            borderColor="neutrals.200"
            borderRadius="8px"
            flex="1"
            h="120px"
            p="32px"
          >
            <SkeletonText noOfLines={10} gap="4" />
          </Box>
        </>
      )}
    </Flex>
  );
}
